import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, FileKey, ShieldCheck, Trash2, Upload } from 'lucide-react';
import type { DigitalCertificate } from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { formatDate, formatDateTime, formatDocument } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { ReadField } from '@/components/ContextPanel';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * Certificado digital A1 (e-CNPJ).
 *
 * É a chave que deixa o sistema buscar sozinho, na SEFAZ, as notas emitidas
 * contra o CNPJ do parque — o fornecedor não precisa mandar XML nenhum.
 * O arquivo e a senha entram uma vez, ficam criptografados e nunca voltam.
 */
export function CertificadoPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const query = useQuery({
    queryKey: ['digital-certificate'],
    queryFn: () => api.get<{ data: DigitalCertificate | null }>('/digital-certificate'),
  });

  const certificate = query.data?.data ?? null;

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/digital-certificate/${id}`),
    onSuccess: () => {
      setRemoving(false);
      void queryClient.invalidateQueries({ queryKey: ['digital-certificate'] });
    },
  });

  return (
    <>
      <WorkspaceHeader
        title="Certificado digital"
        actions={
          can('certificado-digital:criar') ? (
            <Button variant="primary" icon={Upload} onClick={() => setUploading(true)}>
              {certificate ? 'Substituir certificado' : 'Subir certificado'}
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {query.isPending && <LoadingState />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}

        {query.data && !certificate && (
          <EmptyState
            title="Nenhum certificado cadastrado"
            description="Com o A1 (.pfx) da empresa aqui, a Central de Documentos busca as NF-e na SEFAZ sozinha — sem depender do fornecedor enviar o XML."
            action={
              can('certificado-digital:criar') ? (
                <Button variant="primary" icon={Upload} onClick={() => setUploading(true)}>
                  Subir certificado
                </Button>
              ) : undefined
            }
          />
        )}

        {certificate && (
          <>
            {certificate.isExpired && (
              <div className="attention__item attention__item--bad" style={{ display: 'flex', marginBottom: 16 }}>
                <CalendarClock size={15} color={T.red} aria-hidden />
                <span className="attention__text">
                  Este certificado venceu em {formatDate(certificate.expiresAt)}. A busca na SEFAZ
                  está parada até subir o certificado renovado.
                </span>
              </div>
            )}
            {!certificate.isExpired && certificate.daysToExpire <= 30 && (
              <div className="attention__item attention__item--warn" style={{ display: 'flex', marginBottom: 16 }}>
                <CalendarClock size={15} color={T.amber} aria-hidden />
                <span className="attention__text">
                  Vence em {certificate.daysToExpire} dia(s). Peça a renovação ao contador antes de
                  a busca automática parar.
                </span>
              </div>
            )}

            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <FileKey size={18} color={T.mare} aria-hidden />
                <strong style={{ fontSize: 14 }}>{certificate.subjectName}</strong>
                {certificate.isExpired ? (
                  <StatusBadge kind="bad">Vencido</StatusBadge>
                ) : (
                  <StatusBadge kind="ok">Ativo</StatusBadge>
                )}
              </div>

              <div className="grid-2">
                <ReadField label="CNPJ do titular" value={formatDocument(certificate.document)} mono />
                <ReadField label="Arquivo" value={certificate.fileName} />
                <ReadField label="Válido de" value={formatDate(certificate.issuedAt)} mono />
                <ReadField label="Válido até" value={formatDate(certificate.expiresAt)} mono />
                <ReadField
                  label="Última busca na SEFAZ"
                  value={certificate.lastSyncAt ? formatDateTime(certificate.lastSyncAt) : 'Nunca'}
                  mono
                />
                <ReadField label="Último NSU lido" value={certificate.lastNsu} mono />
              </div>

              <p className="muted" style={{ fontSize: 12.5, marginTop: 16 }}>
                Com este certificado no ar, o sistema busca as notas na SEFAZ sozinho às{' '}
                <strong>06:00, 10:00 e 15:00</strong> — o botão "Buscar na SEFAZ" em Documentos
                serve para adiantar quando você não quer esperar o próximo horário.
              </p>
              <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
                O arquivo e a senha ficam criptografados no banco e não voltam por nenhuma tela nem
                pela API. Para trocar, suba o novo — este é desativado automaticamente.
              </p>

              {can('certificado-digital:excluir') && (
                <div style={{ marginTop: 16 }}>
                  <Button variant="danger" icon={Trash2} onClick={() => setRemoving(true)}>
                    Remover certificado
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <UploadModal
        open={uploading}
        replacing={Boolean(certificate)}
        onClose={() => setUploading(false)}
        onDone={() => {
          setUploading(false);
          void queryClient.invalidateQueries({ queryKey: ['digital-certificate'] });
        }}
      />

      <ConfirmDialog
        open={removing}
        destructive
        loading={removeMutation.isPending}
        title="Remover o certificado?"
        confirmLabel="Remover"
        message={
          <>
            A busca automática de notas na SEFAZ para de funcionar até subir outro
            certificado. As notas já importadas continuam onde estão.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(false);
          removeMutation.reset();
        }}
        onConfirm={() => certificate && removeMutation.mutate(certificate.id)}
      />
    </>
  );
}

function UploadModal({
  open,
  replacing,
  onClose,
  onDone,
}: {
  open: boolean;
  replacing: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [fileBase64, setFileBase64] = useState('');
  const [password, setPassword] = useState('');
  const [readError, setReadError] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setFileName('');
      setFileBase64('');
      setPassword('');
      setReadError('');
    }
  }

  const mutation = useMutation({
    mutationFn: () => api.post('/digital-certificate', { fileName, fileBase64, password }),
    onSuccess: onDone,
  });

  const errors = fieldErrors(mutation.error);

  /** Lê o .pfx no navegador e manda em base64 — o arquivo não passa por disco. */
  const pickFile = (file: File | undefined) => {
    if (!file) return;
    setReadError('');
    if (file.size > 1024 * 1024) {
      setReadError('O arquivo passa de 1 MB — confira se é mesmo o .pfx do certificado.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      setFileBase64(result.slice(result.indexOf(',') + 1));
      setFileName(file.name);
    };
    reader.onerror = () => setReadError('Não consegui ler o arquivo. Tente selecionar de novo.');
    reader.readAsDataURL(file);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={replacing ? 'Substituir certificado' : 'Subir certificado A1'}
      description="Arquivo .pfx do e-CNPJ e a senha que o contador forneceu"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={ShieldCheck}
            loading={mutation.isPending}
            disabled={!fileBase64 || password.length === 0}
            onClick={() => mutation.mutate()}
          >
            Validar e salvar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field
          label="Arquivo do certificado (.pfx ou .p12)"
          htmlFor="cert-file"
          error={readError || errors.fileBase64}
        >
          <input
            id="cert-file"
            ref={fileRef}
            className="input"
            type="file"
            accept=".pfx,.p12,application/x-pkcs12"
            onChange={(event) => pickFile(event.target.files?.[0])}
          />
        </Field>

        {fileName && (
          <p className="mono" style={{ fontSize: 12.5, color: T.inkSoft }}>
            {fileName}
          </p>
        )}

        <Field
          label="Senha do certificado"
          htmlFor="cert-password"
          error={errors.password}
          hint="Conferimos na hora: senha errada não passa daqui"
        >
          <input
            id="cert-password"
            className="input"
            type="password"
            autoComplete="off"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <p className="muted" style={{ fontSize: 12.5 }}>
          Só funciona com certificado <strong>A1</strong> (arquivo). O A3 (token ou cartão) não roda
          em servidor — nesse caso, peça ao contador um A1 do mesmo CNPJ.
        </p>
      </FormSection>
    </Modal>
  );
}
